/**
 * @file React hook — useA2FormData.ts: form/state logic for contributor dashboard.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axiosClient from "../../../../../../api/axiosClient";
import { FORM_STAGE_CODES } from "../../../../../../constants/formStages";
import { RawMat, useA2Prefill } from "./useA2Prefill";
import { FormDataResponse } from "../../../../../../interfaces/formInterfaces";
import { useA2EnergyCalculator } from "./useA2EnergyCalculator";
import { getFuelDensities } from "../../../../../../services/a1Service";
import { fetchActiveCalorificValues } from "../../../../../../services/calorificValuesPublicService";
import {
  fetchVehicleCategoryEmissionFactors,
  type VehicleCategoryEmissionFactorPublic,
} from "../../../../../../services/vehicleCategoryEmissionFactorsPublicService";
import {
  getA2FormData,
  getSubmission as getA2Submission,
  saveA2Details,
} from "../../../../../../services/a2Service";
import {
  fetchCatalogRawMaterialsForMaterial,
  normalizeRawMaterialNameKey,
  resolveCatalogRawMaterialIdFromBackgroundRow,
} from "../../shared/catalogRawMaterialResolution";
import {
  findCalorificValueForMix,
  findFuelDensityForMix,
  getFuelMixDropdownLabelForField,
  loadFuelFieldUnitOptions,
  fuelDensityValueAndUnitFromCatalogRow,
  applyFuelDensityUnitFieldChange,
  pairedFuelDensityValueKey,
  parseFuelMixFieldNameWithPrefix,
  pickCalorificUnitFromOptions,
  type UnitSelectOption,
} from "../../shared/fuelMixReferenceAutofill";
import { resolveKgCo2eUnitId } from "../../shared/unitSelectOptions";
import {
  buildA2EmbodiedEnergyValueUpdates,
  buildDefaultA2EmbodiedEnergyUnitUpdates,
  resolveA2EmbodiedEnergyMjUnitSelectValue,
} from "../../../../../../utils/a2FuelEmbodiedEnergy";
import {
  buildA2EmbodiedCarbonQuantityUpdates,
  buildA2TransportCharacterizationUpdates,
  findVehicleCategoryEmissionFactor,
  hydrateA2UnitFieldsBeforeValidation,
} from "../../../../../../utils/a2TransportCarbonCalculations";
import { formatEmbodiedCarbonReferenceQtyFromUnknown, formatEmbodiedCarbonFunctionalUnitFromUnknown } from "../../shared/embodiedCarbonReferenceQtyFormat";
import { A1_PETROL_DIESEL_EMBODIED_ENERGY_CONVERSION_FACTOR } from "../../../../../../constants/a1EmbodiedEnergyFactors";
import { haversineDistanceKm } from "../../../../../../utils/haversineDistanceKm";
import { reverseGeocode } from "../../../../../../utils/useA1Location";
import { toast } from "react-toastify";
import {
  getFirstFormError,
  validateA2Form,
} from "../../shared/validateA2Form";
import { clearFieldErrorsFromMap } from "../../shared/contributorFormValidation";
import { fetchUnitIdToSymbolMap } from "../../../../../../utils/fetchUnitIdToSymbolMap";
import {
  A1A3_CARBON_NUMERATOR_UNIT,
  perFinishedCarbonUnitLabel,
  perFinishedCarbonUnitValue,
  perFinishedEnergyUnitLabel,
  perFinishedEnergyUnitValue,
  resolveFinishedQtyDenominatorSymbol,
} from "../../A1-A3/a1a3SummaryFromSubmission";
import {
  embodiedCarbonFunctionalUnitValidValues,
  normalizeEmbodiedCarbonFunctionalUnitFormValue,
  resolveEmbodiedCarbonFunctionalUnitForSave,
} from "../../shared/contributorPerFunctionalUnitUnits";
import {
  A2_MAX_VEHICLES_PER_RAW_MATERIAL,
  buildA2VehiclePrefillFromGrouped,
  buildA2VehiclePrefillLayout,
  collectVehicleValueKeyPrefixes,
  insertIndexForNewVehicleAtMaterial,
  materialIndexForVehicleFlatRow,
  shiftA2VehicleValuesForInsert,
  shiftA2VehicleValuesForRemove,
  totalVehicleRows,
} from "../utils/a2VehicleLayout";
import {
  A2_MAX_FUELS_PER_RAW_MATERIAL,
  buildA2FuelPrefillFromGrouped,
  buildA2FuelPrefillLayout,
  collectFuelValueKeyPrefixesIncludingEnergy,
  insertIndexForNewFuelAtMaterial,
  shiftA2FuelValuesForInsert,
  shiftA2FuelValuesForRemove,
} from "../utils/a2FuelLayout";
import { normalizeA2FuelMixInValues } from "../utils/a2ApiFormPrefill";

const A2_TRANSPORT_CODE = "A2_TRANSPORT_DETAILS";
const A2_VEHICLE_CODE = "A2_VEHICLE_DETAILS";
const A2_FUEL_TRANSPORT_CODE = "A2_FUEL_TRANSPORT";
const A2_CARBON_CODE = "A2_EMBODIED_CARBON";
const A2_FUEL_MIX_FIELD = "fuelMix_01";

function normalizeA2VehicleCategoryIdsInValues(
  vals: Record<string, string>,
  rows: VehicleCategoryEmissionFactorPublic[]
): Record<string, string> {
  if (!rows.length) return vals;
  const prefix = `vehicleCategory_${A2_VEHICLE_CODE}_`;
  let changed = false;
  const next = { ...vals };
  for (const k of Object.keys(vals)) {
    if (!k.startsWith(prefix)) continue;
    const v = vals[k] ?? "";
    const pub = findVehicleCategoryEmissionFactor(v, rows);
    if (pub && String(v).trim() !== String(pub.id)) {
      next[k] = String(pub.id);
      changed = true;
    }
  }
  return changed ? next : vals;
}

function orderRawMaterialGroupsForNormalized(
  groups: unknown[] | undefined,
  normalized: RawMat[],
  catalogNameToId: Map<string, number>,
  normalizeKey: (s: string) => string
): Record<string, unknown>[] {
  const R = normalized.length;
  if (R <= 0) return [];
  const list = Array.isArray(groups) ? groups : [];
  const used = new Set<number>();
  const out: Record<string, unknown>[] = [];

  for (let m = 0; m < R; m++) {
    const rm = normalized[m];
    const gid = rm.catalogId ?? rm.id;
    let gi = -1;

    if (Number(gid) > 0) {
      gi = list.findIndex(
        (x, idx) =>
          !used.has(idx) &&
          Number((x as { rawMaterialId?: unknown })?.rawMaterialId) ===
            Number(gid)
      );
    }
    if (gi < 0 && rm.name) {
      const mid = catalogNameToId.get(normalizeKey(rm.name));
      if (mid != null) {
        gi = list.findIndex(
          (x, idx) =>
            !used.has(idx) &&
            Number((x as { rawMaterialId?: unknown })?.rawMaterialId) === mid
        );
      }
    }
    if (gi < 0 && rm.name) {
      const nk = normalizeKey(rm.name);
      gi = list.findIndex(
        (x, idx) =>
          !used.has(idx) &&
          normalizeKey(
            String((x as { rawMaterialName?: unknown })?.rawMaterialName ?? "")
          ) === nk
      );
    }

    if (gi >= 0) {
      used.add(gi);
      out.push((list[gi] as Record<string, unknown>) ?? {});
    } else {
      out.push({});
    }
  }
  return out;
}

function sumPerMaterialCounts(counts: number[], endExclusive: number) {
  let s = 0;
  for (let i = 0; i < endExclusive; i++) {
    s += Math.max(0, counts[i] ?? 0);
  }
  return s;
}

type ExtractionCoord = { lat: string; lng: string };

/**
 * Per flat vehicle row (single vehicle per raw material only):
 * trips = ceil((declaredQty ÷ vehicles for that material) ÷ capacity)
 * totalDistanceTravelled = trips × site-to-site distance (km).
 * When a raw material has more than one vehicle, this function does not set these fields — contributor enters trips and total distance manually.
 */
function mergeA2VehicleTripsAndDistance(
  base: Record<string, string>,
  rawMaterials: { qty?: string }[],
  sectionRows: Record<string, number>,
  finishedQuantityFallback: string,
  vehiclesPerRawMaterial: number[],
  manualTotalDistanceByRow: Record<string, boolean> = {},
  manualTripsByRow: Record<string, boolean> = {}
): Record<string, string> {
  const vehicleRows = Math.max(1, sectionRows[A2_VEHICLE_CODE] ?? 1);

  const finishedParsed = parseFloat(
    String(finishedQuantityFallback ?? "").trim() || ""
  );

  const next = { ...base };
  let changed = false;

  const counts =
    vehiclesPerRawMaterial.length > 0
      ? vehiclesPerRawMaterial
      : Array(Math.max(1, rawMaterials.length)).fill(1);

  for (let i = 0; i < vehicleRows; i++) {
    const m = materialIndexForVehicleFlatRow(i, counts);
    const nVehiclesForMat = Math.max(1, counts[m] ?? 1);

    if (nVehiclesForMat > 1) {
      // Manual entry only; do not overwrite with calculated trips × distance
      continue;
    }

    const distStr =
      base[`distanceBetweenSites_${A2_TRANSPORT_CODE}_row${m}`] || "";
    const distanceKm = parseFloat(distStr);
    const capacity = parseFloat(
      String(
        base[`vehicleCapacity_${A2_VEHICLE_CODE}_row${i}`] ?? ""
      ).trim() || ""
    );

    const perRmQty = parseFloat(
      String(rawMaterials[m]?.qty ?? "").trim() || ""
    );
    const declaredQty =
      Number.isFinite(perRmQty) && perRmQty > 0
        ? perRmQty
        : Number.isFinite(finishedParsed) && finishedParsed > 0
          ? finishedParsed
          : NaN;

    const qtyPortion =
      Number.isFinite(declaredQty) && declaredQty > 0
        ? declaredQty / nVehiclesForMat
        : NaN;

    let tripsStr = "0";

    if (
      Number.isFinite(qtyPortion) &&
      qtyPortion > 0 &&
      Number.isFinite(capacity) &&
      capacity > 0
    ) {
      const trips = Math.ceil(qtyPortion / capacity);
      tripsStr = String(trips);
    }

    const tripsKey = `tripsRequired_${A2_VEHICLE_CODE}_row${i}`;
    const totalKey = `totalDistanceTravelled_${A2_VEHICLE_CODE}_row${i}`;
    const rowKey = `row${i}`;

    if (!manualTripsByRow[rowKey] && next[tripsKey] !== tripsStr) {
      next[tripsKey] = tripsStr;
      changed = true;
    }

    const tripsForTotal = manualTripsByRow[rowKey]
      ? parseFloat(String(next[tripsKey] ?? "").trim() || "")
      : parseFloat(tripsStr);
    let totalDistStr = "0";
    if (
      Number.isFinite(tripsForTotal) &&
      tripsForTotal >= 0 &&
      Number.isFinite(distanceKm) &&
      distanceKm >= 0
    ) {
      totalDistStr = (tripsForTotal * distanceKm).toFixed(2);
    }

    if (!manualTotalDistanceByRow[rowKey] && next[totalKey] !== totalDistStr) {
      next[totalKey] = totalDistStr;
      changed = true;
    }
  }

  return changed ? next : base;
}

function mergeA2TransportThenVehicle(
  prev: Record<string, string>,
  extractionRows: ExtractionCoord[],
  sectionRows: Record<string, number>,
  rawMaterials: { qty?: string }[],
  finishedQuantityFallback: string,
  manualDistanceByRow: Record<string, boolean> = {},
  vehiclesPerRawMaterial: number[] = [],
  manualTotalDistanceByRow: Record<string, boolean> = {},
  manualTripsByRow: Record<string, boolean> = {}
): Record<string, string> {
  const afterDist = mergeA2TransportDistances(
    prev,
    extractionRows,
    sectionRows,
    manualDistanceByRow,
    vehiclesPerRawMaterial
  );
  return mergeA2VehicleTripsAndDistance(
    afterDist,
    rawMaterials,
    sectionRows,
    finishedQuantityFallback,
    vehiclesPerRawMaterial,
    manualTotalDistanceByRow,
    manualTripsByRow
  );
}

/** Row keys `row0`, `row1`, …: when true, keep user/saved distance; skip haversine for that row. */
function mergeA2TransportDistances(
  base: Record<string, string>,
  extractionRows: ExtractionCoord[],
  sectionRows: Record<string, number>,
  manualDistanceByRow: Record<string, boolean> = {},
  vehiclesPerRawMaterial: number[] = []
): Record<string, string> {
  const rows = Math.max(1, sectionRows[A2_TRANSPORT_CODE] ?? 1);
  const next = { ...base };
  let changed = false;

  const vCounts =
    vehiclesPerRawMaterial.length >= rows
      ? vehiclesPerRawMaterial.map((c) => Math.max(1, c))
      : Array.from({ length: rows }, () => 1);

  for (let i = 0; i < rows; i++) {
    const rowKey = `row${i}`;
    if (manualDistanceByRow[rowKey]) continue;

    if (Math.max(1, vCounts[i] ?? 1) > 1) {
      continue;
    }

    const ext = extractionRows[i];
    const lat1 = ext ? parseFloat(ext.lat) : NaN;
    const lng1 = ext ? parseFloat(ext.lng) : NaN;
    const lat2 = parseFloat(
      base[`manufacturingLatitude_${A2_TRANSPORT_CODE}_row${i}`] || ""
    );
    const lng2 = parseFloat(
      base[`manufacturingLongitude_${A2_TRANSPORT_CODE}_row${i}`] || ""
    );
    const km = haversineDistanceKm(lat1, lng1, lat2, lng2);
    const key = `distanceBetweenSites_${A2_TRANSPORT_CODE}_row${i}`;
    const val = km != null ? km.toFixed(2) : "";
    if (next[key] !== val) {
      next[key] = val;
      changed = true;
    }
  }

  return changed ? next : base;
}

/** Hook: use A2Form Data. */
export function useA2FormData({ submissionId }: { submissionId?: string }) {
  const [formData, setFormData] = useState<FormDataResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [rawMaterials, setRawMaterials] = useState<RawMat[]>([]);
  const [sectionRows, setSectionRows] = useState<Record<string, number>>({});
  const [values, setValues] = useState<Record<string, string>>({});
  /** Vehicle rows per background raw material (length === rawMaterials.length, each ≥ 1). */
  const [vehiclesPerRawMaterial, setVehiclesPerRawMaterial] = useState<number[]>([1]);
  const vehiclesPerRawMaterialRef = useRef<number[]>([1]);
  /** Material-level fuel rows per background raw material (length === rawMaterials.length, each 1..3). */
  const [fuelsPerRawMaterial, setFuelsPerRawMaterial] = useState<number[]>([1]);
  const fuelsPerRawMaterialRef = useRef<number[]>([1]);

  /** A1 Raw Material Extraction coordinates per row (same index as A2 transport rows). */
  const a1ExtractionRef = useRef<ExtractionCoord[]>([]);
  /** Per transport row: user/saved distance (km) — do not overwrite with haversine. */
  const a2DistanceManualByRowRef = useRef<Record<string, boolean>>({});
  /** Per vehicle row: user/saved total distance (km) — do not overwrite with trips × distance. */
  const a2TotalDistanceManualByRowRef = useRef<Record<string, boolean>>({});
  /** Per vehicle row: user/saved trip count — do not overwrite with calculated trips. */
  const a2TripsManualByRowRef = useRef<Record<string, boolean>>({});
  const sectionRowsRef = useRef<Record<string, number>>({});
  /** Background finished material declared quantity (fallback if raw line qty missing). */
  const backgroundFinishedQtyRef = useRef<string>("");
  /** Same as ref; state drives energy/carbon “per finished qty” summaries. */
  const [backgroundFinishedQuantity, setBackgroundFinishedQuantity] =
    useState<string>("");
  const [backgroundFinishedMaterialUnitSymbol, setBackgroundFinishedMaterialUnitSymbol] =
    useState<string | null>(null);
  const rawMaterialsRef = useRef<{ qty?: string }[]>([]);

  const [vehicleCategoryEmissionFactors, setVehicleCategoryEmissionFactors] =
    useState<VehicleCategoryEmissionFactorPublic[]>([]);
  const vehicleCategoryEmissionFactorsRef = useRef<
    VehicleCategoryEmissionFactorPublic[]
  >([]);
  const [unitsIndexTick, setUnitsIndexTick] = useState(0);

  /** Petrol/diesel MJ path → kWh; replace when Admin exposes this via API. */
  const petrolDieselConversionRef = useRef<number>(
    A1_PETROL_DIESEL_EMBODIED_ENERGY_CONVERSION_FACTOR
  );

  const a2EnergyManualByFuelFlatRowRef = useRef<Record<string, boolean>>({});
  const a2CfManualByMaterialRowRef = useRef<Record<string, boolean>>({});
  const a2CarbonOutputManualByMaterialRowRef = useRef<Record<string, boolean>>(
    {}
  );
  /** Unit string from last auto-applied transport characterization row (per raw material index). */
  const a2CfUnitHintByMaterialRef = useRef<Record<number, string>>({});

  /** Valid `raw_materials.id` for this submission’s finished material (not background row ids). */
  const catalogRawMaterialIdsRef = useRef<Set<number>>(new Set());
  const catalogNameToIdRef = useRef<Map<string, number>>(new Map());

  /** Unit token → id (from GET /units) for calorific unit fallback. */
  const unitIdByTokenRef = useRef<Record<string, number>>({});
  /** Inverse map (unitId → token) for fuel quantity/density unit resolution in EE calculations. */
  const unitTokenByIdRef = useRef<Record<string, string>>({});
  const fuelDensitiesRef = useRef<
    import("../../../../../../services/a1Service").FuelDensityApiRow[]
  >([]);
  const [fuelDensityCatalog, setFuelDensityCatalog] = useState<
    import("../../../../../../services/a1Service").FuelDensityApiRow[]
  >([]);
  const [calorificValuesCatalog, setCalorificValuesCatalog] = useState<
    import("../../../../../../services/calorificValuesPublicService").CalorificValuePublic[]
  >([]);
  const calorificValuesRef = useRef<
    import("../../../../../../services/calorificValuesPublicService").CalorificValuePublic[]
  >([]);
  const a2FuelUnitOptionsRef = useRef<{
    density: UnitSelectOption[];
    calorific: UnitSelectOption[];
  }>({ density: [], calorific: [] });
  const a2CfUnitOptionsRef = useRef<UnitSelectOption[]>([]);
  const a2EmbodiedCarbonUnitOptionsRef = useRef<UnitSelectOption[]>([]);
  const [cfUnitOptionsTick, setCfUnitOptionsTick] = useState(0);

  const { prefillFromSubmission } = useA2Prefill();

  // Save/dirty tracking (same UX as A1)
  const lastSavedRef = useRef<Record<string, string>>({});
  const snapshotInitializedRef = useRef(false);
  const prefillDoneRef = useRef(false);
  /** After load/prefill, capture merged baseline once (avoids stuck isDirty / disabled Next). */
  const pendingBaselineCaptureRef = useRef(false);
  const [hasSavedOnce, setHasSavedOnce] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);

  /** Compare all keys present in either record; treat missing vs "" as equal (init/prefill key skew). */
  const recordsEqual = (a: Record<string, string>, b: Record<string, string>) => {
    const seen: Record<string, boolean> = {};
    const ak = Object.keys(a || {});
    const bk = Object.keys(b || {});
    for (let i = 0; i < ak.length; i++) seen[ak[i]] = true;
    for (let i = 0; i < bk.length; i++) seen[bk[i]] = true;
    const keys = Object.keys(seen);
    for (let i = 0; i < keys.length; i++) {
      const k = keys[i];
      const av = (a as Record<string, string | undefined>)[k] ?? "";
      const bv = (b as Record<string, string | undefined>)[k] ?? "";
      if (av !== bv) return false;
    }
    return true;
  };

  const toNumberOrZero = (v: any) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const toStringOrZero = (v: any) =>
  v !== "" && v !== null && v !== undefined ? String(v) : "0";

  const getEmbodiedEnergyMjUnitId = useCallback(
    () => resolveA2EmbodiedEnergyMjUnitSelectValue(unitIdByTokenRef.current),
    []
  );

  const { energySummaryMj, energyPerDeclaredFinishedMaterialMj } =
    useA2EnergyCalculator({
      formData,
      values,
      sectionRows,
      backgroundFinishedQty: backgroundFinishedQuantity,
    });

  const embodiedCarbonTotalKg = useMemo(() => {
    if (!formData) return 0;
    const carbonSec = formData.formSubSections?.find(
      (s) => s.code === A2_CARBON_CODE
    );
    if (!carbonSec?.code) return 0;
    const cc = carbonSec.code;
    const R = Math.max(0, rawMaterials.length);
    let sum = 0;
    for (let m = 0; m < R; m++) {
      const n = Number(values[`embodiedCarbon_referenceQty_${cc}_row${m}`]);
      if (Number.isFinite(n)) sum += n;
    }
    return Math.round(sum * 1e6) / 1e6;
  }, [formData, rawMaterials.length, values]);

  const embodiedCarbonPerFinishedKg = useMemo(() => {
    const q = parseFloat(String(backgroundFinishedQuantity).trim());
    if (!Number.isFinite(q) || q <= 0) return null;
    if (embodiedCarbonTotalKg <= 0) return null;
    return Math.round((embodiedCarbonTotalKg / q) * 1e6) / 1e6;
  }, [embodiedCarbonTotalKg, backgroundFinishedQuantity]);

  const perFinishedEnergyUnitLabelDisplay = useMemo(
    () => perFinishedEnergyUnitLabel(backgroundFinishedMaterialUnitSymbol),
    [backgroundFinishedMaterialUnitSymbol]
  );

  const perFinishedCarbonUnitLabelDisplay = useMemo(
    () => perFinishedCarbonUnitLabel(backgroundFinishedMaterialUnitSymbol),
    [backgroundFinishedMaterialUnitSymbol]
  );

  const functionalCarbonUnitValue = useMemo(
    () => perFinishedCarbonUnitValue(backgroundFinishedMaterialUnitSymbol),
    [backgroundFinishedMaterialUnitSymbol]
  );

  const a2FuelEnergyFingerprint = useMemo(() => {
    const n = Math.max(0, sectionRows[A2_FUEL_TRANSPORT_CODE] ?? 0);
    let s = "";
    for (let i = 0; i < n; i++) {
      s += `${values[`fuelMix_01_${A2_FUEL_TRANSPORT_CODE}_row${i}`] ?? ""}\u001f${
        values[`fuelQuantity_${A2_FUEL_TRANSPORT_CODE}_row${i}`] ?? ""
      }\u001f${
        values[`fuelQuantity_unit_${A2_FUEL_TRANSPORT_CODE}_row${i}`] ?? ""
      }\u001f${
        values[`fuelDensity_${A2_FUEL_TRANSPORT_CODE}_row${i}`] ?? ""
      }\u001f${
        values[`fuelDensity_unit_${A2_FUEL_TRANSPORT_CODE}_row${i}`] ?? ""
      }\u001f${
        values[`calorificValue_${A2_FUEL_TRANSPORT_CODE}_row${i}`] ?? ""
      }|`;
    }
    return s;
  }, [sectionRows, values]);

  const a2TransportCarbonAutoFingerprint = useMemo(() => {
    if (!formData) return "";
    const R = Math.max(0, rawMaterials.length);
    const vCounts =
      vehiclesPerRawMaterial.length >= R
        ? vehiclesPerRawMaterial.map((c) => Math.max(1, c))
        : Array.from({ length: R }, () => 1);
    let s = "";
    for (let m = 0; m < R; m++) {
      s += `${values[`distanceBetweenSites_${A2_TRANSPORT_CODE}_row${m}`] ?? ""}\u001f${
        rawMaterials[m]?.qty ?? ""
      }\u001f${
        values[`characterizationFactor_${A2_CARBON_CODE}_row${m}`] ?? ""
      }\u001f`;
      const vStart = sumPerMaterialCounts(vCounts, m);
      const nV = Math.max(1, vCounts[m] ?? 1);
      if (nV <= 1) {
        s += `${values[`vehicleCapacity_${A2_VEHICLE_CODE}_row${vStart}`] ?? ""}\u001f${
          values[`totalDistanceTravelled_${A2_VEHICLE_CODE}_row${vStart}`] ?? ""
        }\u001f${values[`vehicleCategory_${A2_VEHICLE_CODE}_row${vStart}`] ?? ""}#`;
      } else {
        for (let j = 0; j < nV; j++) {
          const flat = vStart + j;
          s += `${values[`vehicleCategory_${A2_VEHICLE_CODE}_row${flat}`] ?? ""}\u001f${
            values[`vehicleCapacity_${A2_VEHICLE_CODE}_row${flat}`] ?? ""
          }\u001f${
            values[`totalDistanceTravelled_${A2_VEHICLE_CODE}_row${flat}`] ?? ""
          };`;
        }
        s += "#";
      }
    }
    s += `vc${vehicleCategoryEmissionFactors.length}`;
    return s;
  }, [
    formData,
    values,
    rawMaterials,
    vehiclesPerRawMaterial,
    vehicleCategoryEmissionFactors.length,
  ]);

  useEffect(() => {
    sectionRowsRef.current = sectionRows;
  }, [sectionRows]);

  useEffect(() => {
    rawMaterialsRef.current = rawMaterials;
  }, [rawMaterials]);

  useEffect(() => {
    vehiclesPerRawMaterialRef.current = vehiclesPerRawMaterial;
  }, [vehiclesPerRawMaterial]);

  useEffect(() => {
    fuelsPerRawMaterialRef.current = fuelsPerRawMaterial;
  }, [fuelsPerRawMaterial]);

  useEffect(() => {
    backgroundFinishedQtyRef.current = backgroundFinishedQuantity;
  }, [backgroundFinishedQuantity]);

  useEffect(() => {
    vehicleCategoryEmissionFactorsRef.current = vehicleCategoryEmissionFactors;
  }, [vehicleCategoryEmissionFactors]);

  /** Map legacy vehicleCategory labels from saved payloads to stable id strings. */
  useEffect(() => {
    if (loading || !vehicleCategoryEmissionFactors.length) return;
    setValues((prev) => {
      const next = normalizeA2VehicleCategoryIdsInValues(
        prev,
        vehicleCategoryEmissionFactors
      );
      return next === prev ? prev : next;
    });
  }, [loading, vehicleCategoryEmissionFactors]);

  /** Resolve legacy fuel mix labels/tokens to CMS dropdown values after load. */
  useEffect(() => {
    if (loading || !formData) return;
    setValues((prev) => {
      const next = normalizeA2FuelMixInValues(prev, formData);
      return next === prev ? prev : next;
    });
  }, [loading, formData]);

  /** Keep saved baseline in sync when emission-factor rows load after initial baseline capture. */
  useEffect(() => {
    if (loading || !vehicleCategoryEmissionFactors.length) return;
    if (!snapshotInitializedRef.current) return;
    const cur = lastSavedRef.current;
    const patched = normalizeA2VehicleCategoryIdsInValues(
      cur,
      vehicleCategoryEmissionFactors
    );
    if (patched === cur) return;
    lastSavedRef.current = patched;
    setIsDirty(!recordsEqual(values, lastSavedRef.current));
  }, [loading, vehicleCategoryEmissionFactors, values]);

  /* ======================================================
     AUTO: embodied energy (fuel rows) + transport CF + carbon (single merge)
  ====================================================== */
  useEffect(() => {
    if (!formData || loading) return;
    setValues((prev) => {
      const unitU = buildDefaultA2EmbodiedEnergyUnitUpdates(
        formData,
        prev,
        sectionRows,
        { mjUnitId: getEmbodiedEnergyMjUnitId() }
      );
      const energyU = buildA2EmbodiedEnergyValueUpdates(
        formData,
        { ...prev, ...unitU },
        sectionRows,
        {
          mjUnitId: getEmbodiedEnergyMjUnitId(),
          manualByFlatFuelRow: a2EnergyManualByFuelFlatRowRef.current,
          unitTokenById: unitTokenByIdRef.current,
        }
      );
      let base = { ...prev, ...unitU, ...energyU };

      const validCfUnitValues = new Set(
        a2CfUnitOptionsRef.current.map((o) => String(o.value))
      );
      if (validCfUnitValues.size > 0) {
        for (let m = 0; m < rawMaterials.length; m++) {
          const unitKey = `characterizationFactor_unit_${A2_CARBON_CODE}_row${m}`;
          const current = String(base[unitKey] ?? "").trim();
          if (
            current &&
            !validCfUnitValues.has(current) &&
            !/^\d+$/.test(current)
          ) {
            base = { ...base, [unitKey]: "" };
          }
        }
      }

      const validEmbodiedCarbonUnitValues = new Set(
        a2EmbodiedCarbonUnitOptionsRef.current.map((o) => String(o.value))
      );
      const validFunctionalCarbonUnitValues = embodiedCarbonFunctionalUnitValidValues(
        backgroundFinishedMaterialUnitSymbol,
        Array.from(validEmbodiedCarbonUnitValues)
      );
      if (validEmbodiedCarbonUnitValues.size > 0 || validFunctionalCarbonUnitValues.size > 0) {
        for (let m = 0; m < rawMaterials.length; m++) {
          for (const suffix of [
            "embodiedCarbon_referenceQty_unit",
            "embodiedCarbon_functionalUnit_unit",
          ] as const) {
            const unitKey = `${suffix}_${A2_CARBON_CODE}_row${m}`;
            const current = String(base[unitKey] ?? "").trim();
            const validValues =
              suffix === "embodiedCarbon_functionalUnit_unit"
                ? validFunctionalCarbonUnitValues
                : validEmbodiedCarbonUnitValues;
            if (
              current &&
              validValues.size > 0 &&
              !validValues.has(current) &&
              !/^\d+$/.test(current)
            ) {
              base = { ...base, [unitKey]: "" };
            }
          }
        }
      }

      const { updates: cfU, cfUnitByMaterial } =
        buildA2TransportCharacterizationUpdates(
          base,
          formData,
          rawMaterials.length,
          vehiclesPerRawMaterial,
          A2_CARBON_CODE,
          vehicleCategoryEmissionFactors,
          a2CfManualByMaterialRowRef.current,
          unitIdByTokenRef.current
        );
      for (const [kStr, unitStr] of Object.entries(cfUnitByMaterial)) {
        const mi = Number(kStr);
        if (Number.isFinite(mi)) {
          a2CfUnitHintByMaterialRef.current[mi] = String(unitStr ?? "");
        }
      }
      base = { ...base, ...cfU };

      const carbU = buildA2EmbodiedCarbonQuantityUpdates(
        base,
        rawMaterials,
        A2_CARBON_CODE,
        a2CfUnitHintByMaterialRef.current,
        a2CarbonOutputManualByMaterialRowRef.current,
        vehiclesPerRawMaterial,
        vehicleCategoryEmissionFactors,
        resolveKgCo2eUnitId(unitIdByTokenRef.current),
        functionalCarbonUnitValue
      );
      const out = { ...base, ...carbU };

      const keys = new Set([
        ...Object.keys(unitU),
        ...Object.keys(energyU),
        ...Object.keys(cfU),
        ...Object.keys(carbU),
      ]);
      let same = true;
      keys.forEach((k) => {
        if (!same) return;
        if (prev[k] !== out[k]) same = false;
      });
      return same ? prev : out;
    });
  }, [
    a2FuelEnergyFingerprint,
    a2TransportCarbonAutoFingerprint,
    vehicleCategoryEmissionFactors,
    formData,
    loading,
    rawMaterials,
    sectionRows,
    unitsIndexTick,
    cfUnitOptionsTick,
    vehiclesPerRawMaterial,
    getEmbodiedEnergyMjUnitId,
    backgroundFinishedMaterialUnitSymbol,
    functionalCarbonUnitValue,
  ]);

  useLayoutEffect(() => {
    if (loading || !formData || !backgroundFinishedMaterialUnitSymbol?.trim()) {
      return;
    }
    const kgCo2eUnitId = resolveKgCo2eUnitId(unitIdByTokenRef.current);
    const perUnit = perFinishedCarbonUnitValue(backgroundFinishedMaterialUnitSymbol);
    if (!perUnit) return;

    setValues((prev) => {
      const updates: Record<string, string> = {};
      for (let m = 0; m < rawMaterials.length; m++) {
        const unitKey = `embodiedCarbon_functionalUnit_unit_${A2_CARBON_CODE}_row${m}`;
        const normalized = normalizeEmbodiedCarbonFunctionalUnitFormValue(
          prev[unitKey] ?? "",
          backgroundFinishedMaterialUnitSymbol,
          kgCo2eUnitId
        );
        if (normalized && prev[unitKey] !== normalized) {
          updates[unitKey] = normalized;
        }
      }
      if (!Object.keys(updates).length) return prev;
      return { ...prev, ...updates };
    });
  }, [
    loading,
    formData,
    backgroundFinishedMaterialUnitSymbol,
    rawMaterials.length,
    unitsIndexTick,
  ]);

  /* ======================================================
     LOAD FORM + PREFILL
  ====================================================== */
  useEffect(() => {
    (async () => {
      setLoading(true);
      a1ExtractionRef.current = [];
      a2DistanceManualByRowRef.current = {};
      a2TotalDistanceManualByRowRef.current = {};
      a2TripsManualByRowRef.current = {};
      backgroundFinishedQtyRef.current = "";
      setBackgroundFinishedQuantity("");
      setBackgroundFinishedMaterialUnitSymbol(null);
      setVehicleCategoryEmissionFactors([]);
      a2EnergyManualByFuelFlatRowRef.current = {};
      a2CfManualByMaterialRowRef.current = {};
      a2CarbonOutputManualByMaterialRowRef.current = {};
      a2CfUnitHintByMaterialRef.current = {};
      setVehiclesPerRawMaterial([1]);
      setFuelsPerRawMaterial([1]);
      try {
        // reset tracking for new load
        snapshotInitializedRef.current = false;
        prefillDoneRef.current = false;
        pendingBaselineCaptureRef.current = false;
        lastSavedRef.current = {};
        setIsDirty(false);
        setHasSavedOnce(false);
        catalogRawMaterialIdsRef.current = new Set();
        catalogNameToIdRef.current = new Map();

        const formResult = await getA2FormData(
          FORM_STAGE_CODES.A2_RAW_MATERIAL_TRANSPORT
        );

        if (!formResult.success || !formResult.data) {
          const errorMsg = formResult.error || "Failed to load form data";
          setError(errorMsg);
          toast.error(errorMsg);
          setLoading(false);
          return;
        }

        const form: FormDataResponse = formResult.data;
        setFormData(form);

        // Initialize empty values
        const initValues: Record<string, string> = {};
        form.formSubSections?.flatMap(s => s.fields)
          .forEach(f => initValues[f.name] = "");
        setValues(initValues);

        // Initialize default rows
        const initialRows: Record<string, number> = {};
        form.formSubSections?.forEach((s) => {
          if (s.code) initialRows[s.code] = 1;
        });
        setSectionRows(initialRows);

        void (async () => {
          try {
            const unitsRes = await axiosClient.get("/units");
            const raw = unitsRes?.data?.data ?? unitsRes?.data;
            const map: Record<string, number> = {};
            const byId: Record<string, string> = {};
            const normalizeToken = (s: unknown) => {
              const rawStr = String(s ?? "").trim().toLowerCase();
              if (!rawStr) return "";
              const fixed = rawStr
                .replaceAll("co₂", "co2")
                .replaceAll("₂", "2")
                .replace(/\s+/g, "");
              return fixed.replace(/[^a-z0-9/()+\-.]/g, "");
            };
            const addToken = (token: unknown, id: unknown) => {
              const t = String(token ?? "").trim().toLowerCase();
              const tn = normalizeToken(token);
              const n = Number(id);
              if (!t) return;
              if (!Number.isFinite(n)) return;
              if (!map[t]) map[t] = n;
              if (tn && !map[tn]) map[tn] = n;
            };
            const consumeUnit = (u: any) => {
              if (!u) return;
              const id = u.id;
              addToken(u.symbol, id);
              addToken(u.unitSymbol, id);
              addToken(u.name, id);
              addToken(u.displayName, id);
              if (id != null) {
                const idStr = String(id);
                const pick =
                  String(u.symbol ?? u.unitSymbol ?? "").trim() ||
                  String(u.name ?? u.displayName ?? "").trim();
                if (pick && !byId[idStr]) byId[idStr] = pick;
              }
            };
            if (Array.isArray(raw)) {
              raw.forEach((propertyOrUnit: any) => {
                if (Array.isArray(propertyOrUnit?.units)) {
                  propertyOrUnit.units.forEach(consumeUnit);
                } else {
                  consumeUnit(propertyOrUnit);
                }
              });
            } else if (raw && Array.isArray((raw as any).units)) {
              (raw as any).units.forEach(consumeUnit);
            }
            unitIdByTokenRef.current = map;
            unitTokenByIdRef.current = byId;
            setUnitsIndexTick((t) => t + 1);
          } catch (e) {
            console.warn("[A2] failed to load units index", e);
          }

          const [fdResult, cvResult, vcResult] = await Promise.allSettled([
            getFuelDensities(),
            fetchActiveCalorificValues(),
            fetchVehicleCategoryEmissionFactors(),
          ]);
          if (fdResult.status === "fulfilled") {
            fuelDensitiesRef.current = fdResult.value;
            setFuelDensityCatalog(fdResult.value);
          } else {
            console.warn("[A2] fuel densities unavailable", fdResult.reason);
            fuelDensitiesRef.current = [];
            setFuelDensityCatalog([]);
          }
          if (cvResult.status === "fulfilled") {
            calorificValuesRef.current = cvResult.value;
            setCalorificValuesCatalog(cvResult.value);
          } else {
            console.warn("[A2] calorific values unavailable", cvResult.reason);
            calorificValuesRef.current = [];
            setCalorificValuesCatalog([]);
          }
          if (vcResult.status === "fulfilled") {
            setVehicleCategoryEmissionFactors(vcResult.value);
          } else {
            console.warn(
              "[A2] vehicle category emission factors unavailable",
              vcResult.reason
            );
            setVehicleCategoryEmissionFactors([]);
          }

          try {
            const fuelSub = form.formSubSections?.find(
              (s) => s.code === A2_FUEL_TRANSPORT_CODE
            );
            const carbonSub = form.formSubSections?.find(
              (s) => s.code === A2_CARBON_CODE
            );
            const [densityOpts, calorificOpts, cfUnitOpts, embodiedCarbonUnitOpts] =
              await Promise.all([
              loadFuelFieldUnitOptions(fuelSub, "fuelDensity_unit"),
              loadFuelFieldUnitOptions(fuelSub, "calorificValue_unit"),
              loadFuelFieldUnitOptions(carbonSub, "characterizationFactor_unit"),
              loadFuelFieldUnitOptions(
                carbonSub,
                "embodiedCarbon_referenceQty_unit"
              ),
            ]);
            a2FuelUnitOptionsRef.current = {
              density: densityOpts,
              calorific: calorificOpts,
            };
            a2CfUnitOptionsRef.current = cfUnitOpts;
            a2EmbodiedCarbonUnitOptionsRef.current = embodiedCarbonUnitOpts;
            setCfUnitOptionsTick((t) => t + 1);
          } catch {
            a2FuelUnitOptionsRef.current = { density: [], calorific: [] };
            a2CfUnitOptionsRef.current = [];
            a2EmbodiedCarbonUnitOptionsRef.current = [];
          }
        })();

        // Only try to load submission data if submissionId is provided
        if (!submissionId) {
          lastSavedRef.current = initValues;
          snapshotInitializedRef.current = true;
          prefillDoneRef.current = true;
          setHasSavedOnce(false);
          setIsDirty(false);
          setLoading(false);
          return;
        }

        // Try to load submission data, but don't block form rendering if it fails
        const subResult = await getA2Submission(submissionId, {
          params: { a2Grouped: true },
        });
        if (!subResult.success || !subResult.data) {
          const errorMsg = subResult.error || "Failed to load submission data";
          setError(errorMsg);
          toast.error(errorMsg);
          // Continue to show form even if submission data fails to load
        } else {
          const a1Ext = subResult.data.A1_Extraction;
          if (Array.isArray(a1Ext) && a1Ext.length > 0) {
            a1ExtractionRef.current = a1Ext.map((e: any) => ({
              lat: String(e.latitude ?? ""),
              lng: String(e.longitude ?? ""),
            }));
          }

          const bgFinished =
            subResult.data.background?.finishedQuantity ??
            subResult.data.background?.finished_quantity;
          if (bgFinished != null && bgFinished !== "") {
            const fq = String(bgFinished);
            backgroundFinishedQtyRef.current = fq;
            setBackgroundFinishedQuantity(fq);
          }

          try {
            const unitMap = await fetchUnitIdToSymbolMap();
            setBackgroundFinishedMaterialUnitSymbol(
              resolveFinishedQtyDenominatorSymbol(subResult.data, unitMap)
            );
          } catch {
            setBackgroundFinishedMaterialUnitSymbol(
              resolveFinishedQtyDenominatorSymbol(subResult.data, {})
            );
          }

          const bg = subResult.data.backgroundRawMats || [];

          const backgroundMaterialRaw =
            subResult.data?.background?.materialId ??
            subResult.data?.background?.material_id;
          const backgroundMaterialNum = Number(backgroundMaterialRaw);

          let catalogList: { id: number; name: string }[] = [];
          if (Number.isFinite(backgroundMaterialNum)) {
            try {
              catalogList = await fetchCatalogRawMaterialsForMaterial(
                backgroundMaterialNum
              );
            } catch (catErr) {
              console.warn(
                "[A2] Failed to load catalog raw materials:",
                catErr
              );
            }
          }

          catalogRawMaterialIdsRef.current = new Set(
            catalogList.map((c) => c.id)
          );
          const nameToCatalogId = new Map<string, number>();
          for (const c of catalogList) {
            const k = normalizeRawMaterialNameKey(c.name);
            if (k && !nameToCatalogId.has(k)) nameToCatalogId.set(k, c.id);
          }
          catalogNameToIdRef.current = nameToCatalogId;

          const normalized: RawMat[] = bg.map((r: any, idx: number) => {
            const catalogId = resolveCatalogRawMaterialIdFromBackgroundRow(
              r as Record<string, unknown>,
              catalogList
            );
            const nameRaw =
              r.rawMaterialName ??
              r.raw_material_name ??
              r.materialName ??
              r.material_name ??
              r.name ??
              "";
            const name =
              typeof nameRaw === "string"
                ? nameRaw.trim()
                : String(nameRaw ?? "");
            const idForSelect =
              catalogId != null && catalogId > 0 ? catalogId : 0;
            return {
              id: idForSelect,
              catalogId,
              name,
              qty: r.qty ? String(r.qty) : "",
              unitId: r.unitId,
              idx,
            };
          });

          setRawMaterials(normalized);

          const R = Math.max(1, normalized.length);
          const groupsRaw = subResult.data?.rawMaterialGroups;
          const useGrouped =
            Array.isArray(groupsRaw) && groupsRaw.length > 0;
          const ordered = useGrouped
            ? orderRawMaterialGroupsForNormalized(
                groupsRaw,
                normalized,
                catalogNameToIdRef.current,
                normalizeRawMaterialNameKey
              )
            : [];

          const submissionForPrefill = useGrouped
            ? {
                ...subResult.data,
                A2_Transport: ordered.map(
                  (g) =>
                    (g?.transport as Record<string, unknown> | undefined) ?? {}
                ),
                A2_Carbon: ordered.map(
                  (g) =>
                    (g?.carbon as Record<string, unknown> | undefined) ?? {}
                ),
              }
            : subResult.data;

          const vehicleLayout = useGrouped
            ? buildA2VehiclePrefillFromGrouped(ordered, R)
            : buildA2VehiclePrefillLayout(
                subResult.data?.A2_Vehicle,
                normalized,
                catalogRawMaterialIdsRef.current,
                catalogNameToIdRef.current,
                normalizeRawMaterialNameKey
              );

          const fuelLayout = useGrouped
            ? buildA2FuelPrefillFromGrouped(ordered, R)
            : buildA2FuelPrefillLayout(
                subResult.data?.A2_Fuel,
                normalized,
                catalogRawMaterialIdsRef.current,
                catalogNameToIdRef.current,
                normalizeRawMaterialNameKey
              );

          setVehiclesPerRawMaterial(vehicleLayout.counts);
          setFuelsPerRawMaterial(fuelLayout.counts);

          prefillFromSubmission(
            form.formSubSections || [],
            submissionForPrefill,
            normalized,
            setValues,
            setSectionRows,
            catalogRawMaterialIdsRef.current,
            vehicleLayout,
            fuelLayout
          );

          setValues((prev) => {
            const energyArr = subResult.data?.A2_Energy;
            let next = { ...prev };
            if (Array.isArray(energyArr) && energyArr.length > 0) {
              const counts = fuelLayout.counts;
              for (let m = 0; m < normalized.length; m++) {
                const firstFlat = sumPerMaterialCounts(counts, m);
                const e = energyArr[m];
                if (!e || typeof e !== "object") continue;
                const rowKey = `row${firstFlat}`;
                const eeKey = `embodiedEnergy_${A2_FUEL_TRANSPORT_CODE}_${rowKey}`;
                if (String(next[eeKey] ?? "").trim() !== "") continue;
                next[eeKey] = String(
                  (e as { embodiedEnergy?: unknown }).embodiedEnergy ?? ""
                );
                const u = (e as { embodiedEnergyUnitId?: unknown })
                  .embodiedEnergyUnitId;
                next[`embodiedEnergy_unit_${A2_FUEL_TRANSPORT_CODE}_${rowKey}`] =
                  u != null && String(u).trim() !== "" ? String(u) : "";
              }
            }
            next = normalizeA2FuelMixInValues(next, form);

            const vehicleFlatCount = Math.max(1, vehicleLayout.flatItems.length);
            const calculatedTotals = mergeA2VehicleTripsAndDistance(
              next,
              normalized,
              { [A2_VEHICLE_CODE]: vehicleFlatCount },
              backgroundFinishedQtyRef.current,
              vehicleLayout.counts,
              {},
              {}
            );
            for (let i = 0; i < vehicleFlatCount; i++) {
              const tripsKey = `tripsRequired_${A2_VEHICLE_CODE}_row${i}`;
              const savedTrips = String(next[tripsKey] ?? "").trim();
              const calcTrips = String(calculatedTotals[tripsKey] ?? "").trim();
              if (savedTrips !== "" && savedTrips !== calcTrips) {
                a2TripsManualByRowRef.current[`row${i}`] = true;
              }
              const key = `totalDistanceTravelled_${A2_VEHICLE_CODE}_row${i}`;
              const savedVal = String(next[key] ?? "").trim();
              const calcVal = String(calculatedTotals[key] ?? "").trim();
              if (savedVal !== "" && savedVal !== calcVal) {
                a2TotalDistanceManualByRowRef.current[`row${i}`] = true;
              }
            }

            return next;
          });

          const transportSaved = useGrouped
            ? ordered.map(
                (g) =>
                  (g?.transport as { distanceKm?: unknown } | undefined) ?? {}
              )
            : subResult.data?.A2_Transport;
          if (Array.isArray(transportSaved)) {
            transportSaved.forEach(
              (item: { distanceKm?: unknown }, i: number) => {
                if (String(item?.distanceKm ?? "").trim() !== "") {
                  a2DistanceManualByRowRef.current[`row${i}`] = true;
                }
              }
            );
          }

          const hasExistingA2 =
            useGrouped ||
            (Array.isArray(subResult.data?.A2_Transport) &&
              subResult.data.A2_Transport.length > 0) ||
            (Array.isArray(subResult.data?.A2_Vehicle) &&
              subResult.data.A2_Vehicle.length > 0) ||
            (Array.isArray(subResult.data?.A2_Fuel) &&
              subResult.data.A2_Fuel.length > 0) ||
            (Array.isArray(subResult.data?.A2_Energy) &&
              subResult.data.A2_Energy.length > 0) ||
            (Array.isArray(subResult.data?.A2_Carbon) &&
              subResult.data.A2_Carbon.length > 0);
          setHasSavedOnce(Boolean(hasExistingA2));
        }
        prefillDoneRef.current = true;
        pendingBaselineCaptureRef.current = true;
      } catch (e: any) {
        const errorMsg = e?.response?.data?.message || e?.message || "An unexpected error occurred";
        setError(errorMsg);
        toast.error(errorMsg);
      } finally {
        setLoading(false);
      }
    })();
  }, [submissionId]);

  // Transport distance (km) + vehicle trips / total km — apply before paint.
  useLayoutEffect(() => {
    if (loading || !formData) return;
    setValues((prev) =>
      mergeA2TransportThenVehicle(
        prev,
        a1ExtractionRef.current,
        sectionRows,
        rawMaterials,
        backgroundFinishedQtyRef.current,
        a2DistanceManualByRowRef.current,
        vehiclesPerRawMaterialRef.current,
        a2TotalDistanceManualByRowRef.current,
        a2TripsManualByRowRef.current
      )
    );
  }, [loading, formData, sectionRows, rawMaterials, vehiclesPerRawMaterial]);

  // One-shot baseline after load: run after layout merge commits so lastSaved matches canonical merged state.
  useEffect(() => {
    if (loading || !formData || !prefillDoneRef.current) return;
    if (!pendingBaselineCaptureRef.current) return;
    pendingBaselineCaptureRef.current = false;

    queueMicrotask(() => {
      setValues((prev) => {
        let merged = mergeA2TransportThenVehicle(
          prev,
          a1ExtractionRef.current,
          sectionRowsRef.current,
          rawMaterialsRef.current,
          backgroundFinishedQtyRef.current,
          a2DistanceManualByRowRef.current,
          vehiclesPerRawMaterialRef.current,
          a2TotalDistanceManualByRowRef.current,
          a2TripsManualByRowRef.current
        );
        merged = normalizeA2VehicleCategoryIdsInValues(
          merged,
          vehicleCategoryEmissionFactorsRef.current
        );
        merged = normalizeA2FuelMixInValues(merged, formData);
        lastSavedRef.current = { ...merged };
        snapshotInitializedRef.current = true;
        return merged;
      });
    });
  }, [loading, formData, sectionRows, rawMaterials, vehiclesPerRawMaterial]);

  // Track dirty state
  useEffect(() => {
    if (!snapshotInitializedRef.current) return;
    const saved = lastSavedRef.current || {};
    setIsDirty(!recordsEqual(values, saved));
  }, [values]);

  /* ======================================================
     HANDLE CHANGE
  ====================================================== */
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setErrors((prev) => clearFieldErrorsFromMap(prev, name));

    if (name.includes(`_${A2_FUEL_TRANSPORT_CODE}_`)) {
      const rm = name.match(/_row(\d+)$/);
      if (rm) {
        const rk = `row${rm[1]}`;
        if (
          name.startsWith("embodiedEnergy_") ||
          name.startsWith("embodiedEnergy_unit_")
        ) {
          a2EnergyManualByFuelFlatRowRef.current[rk] = true;
        }
        if (
          name.startsWith("fuelMix_01_") ||
          name.startsWith("fuelQuantity_") ||
          name.startsWith("fuelDensity_") ||
          name.startsWith("calorificValue_")
        ) {
          delete a2EnergyManualByFuelFlatRowRef.current[rk];
        }
      }
    }

    if (name.includes(`_${A2_CARBON_CODE}_`)) {
      const rm = name.match(/_row(\d+)$/);
      if (rm) {
        const mr = `row${rm[1]}`;
        if (name.startsWith("characterizationFactor_")) {
          a2CfManualByMaterialRowRef.current[mr] = true;
        }
        if (
          name.startsWith("embodiedCarbon_referenceQty_") ||
          name.startsWith("embodiedCarbon_functionalUnit_")
        ) {
          a2CarbonOutputManualByMaterialRowRef.current[mr] = true;
        }
      }
    }

    if (
      name.startsWith("vehicleCategory_") &&
      name.includes(`_${A2_VEHICLE_CODE}_`)
    ) {
      const rm = name.match(/_row(\d+)$/);
      if (rm) {
        const flat = Number(rm[1]);
        if (Number.isFinite(flat)) {
          const matIdx = materialIndexForVehicleFlatRow(
            flat,
            vehiclesPerRawMaterialRef.current
          );
          delete a2CfManualByMaterialRowRef.current[`row${matIdx}`];
        }
      }
    }

    setValues((prev) => {
      const densityKey = pairedFuelDensityValueKey(name);
      if (densityKey) {
        return applyFuelDensityUnitFieldChange(
          prev,
          name,
          value,
          densityKey,
          a2FuelUnitOptionsRef.current.density
        );
      }

      const next = { ...prev, [name]: value };

      // Handle location fetching (same as A1 and PublicationData)
      if (
        name.startsWith("manufacturingLatitude_") ||
        name.startsWith("manufacturingLongitude_")
      ) {
        const parts = name.split("_");
        const code = parts.slice(1, -1).join("_");
        const row = parts.pop()!;

        const latKey = `manufacturingLatitude_${code}_${row}`;
        const lngKey = `manufacturingLongitude_${code}_${row}`;
        const latStr = next[latKey];
        const lngStr = next[lngKey];

        // Only fetch if both lat and lng are available
        if (latStr && lngStr) {
          const lat = Number(latStr);
          const lng = Number(lngStr);

          // Format validation: check if valid numbers and within valid ranges
          const isValidLat =
            Number.isFinite(lat) && lat >= -90 && lat <= 90;
          const isValidLng =
            Number.isFinite(lng) && lng >= -180 && lng <= 180;

          // Only fetch if both are valid
          if (isValidLat && isValidLng) {
            reverseGeocode(lat, lng).then((data) => {
              const address = data?.address || {};

              setValues((p) => {
                const withAddr = {
                  ...p,
                  [`state_${code}_${row}`]:
                    address.state || address.region || address.city || "",
                  [`district_${code}_${row}`]:
                    address.district ||
                    address.city_district ||
                    address.county ||
                    address.state_district ||
                    "N/A",
                  [`pincode_${code}_${row}`]:
                    address.postcode || "",
                };
                return mergeA2TransportThenVehicle(
                  withAddr,
                  a1ExtractionRef.current,
                  sectionRowsRef.current,
                  rawMaterialsRef.current,
                  backgroundFinishedQtyRef.current,
                  a2DistanceManualByRowRef.current,
                  vehiclesPerRawMaterialRef.current,
                  a2TotalDistanceManualByRowRef.current,
                  a2TripsManualByRowRef.current
                );
              });
            }).catch((err) => {
              console.error("Reverse geocoding error:", err);
              // Don't show error to user, just log it
            });
          } else {
            const cleared = {
              ...next,
              [`state_${code}_${row}`]: "",
              [`district_${code}_${row}`]: "",
            };
            return mergeA2TransportThenVehicle(
              cleared,
              a1ExtractionRef.current,
              sectionRowsRef.current,
              rawMaterialsRef.current,
              backgroundFinishedQtyRef.current,
              a2DistanceManualByRowRef.current,
              vehiclesPerRawMaterialRef.current,
              a2TotalDistanceManualByRowRef.current,
              a2TripsManualByRowRef.current
            );
          }
        }

        return mergeA2TransportThenVehicle(
          next,
          a1ExtractionRef.current,
          sectionRowsRef.current,
          rawMaterialsRef.current,
          backgroundFinishedQtyRef.current,
          a2DistanceManualByRowRef.current,
          vehiclesPerRawMaterialRef.current,
          a2TotalDistanceManualByRowRef.current,
          a2TripsManualByRowRef.current
        );
      }

      if (name.startsWith("distanceBetweenSites_")) {
        const m = name.match(/_row(\d+)$/);
        if (m) {
          const rk = `row${m[1]}`;
          if (String(value).trim() === "") {
            delete a2DistanceManualByRowRef.current[rk];
          } else {
            a2DistanceManualByRowRef.current[rk] = true;
          }
        }
        return mergeA2TransportThenVehicle(
          next,
          a1ExtractionRef.current,
          sectionRowsRef.current,
          rawMaterialsRef.current,
          backgroundFinishedQtyRef.current,
          a2DistanceManualByRowRef.current,
          vehiclesPerRawMaterialRef.current,
          a2TotalDistanceManualByRowRef.current,
          a2TripsManualByRowRef.current
        );
      }

      if (name.startsWith("totalDistanceTravelled_")) {
        const m = name.match(/_row(\d+)$/);
        if (m) {
          const rk = `row${m[1]}`;
          if (String(value).trim() === "") {
            delete a2TotalDistanceManualByRowRef.current[rk];
          } else {
            a2TotalDistanceManualByRowRef.current[rk] = true;
          }
        }
        if (String(value).trim() === "") {
          return mergeA2TransportThenVehicle(
            next,
            a1ExtractionRef.current,
            sectionRowsRef.current,
            rawMaterialsRef.current,
            backgroundFinishedQtyRef.current,
            a2DistanceManualByRowRef.current,
            vehiclesPerRawMaterialRef.current,
            a2TotalDistanceManualByRowRef.current,
            a2TripsManualByRowRef.current
          );
        }
        return next;
      }

      if (name.startsWith("tripsRequired_")) {
        const m = name.match(/_row(\d+)$/);
        if (m) {
          const rk = `row${m[1]}`;
          if (String(value).trim() === "") {
            delete a2TripsManualByRowRef.current[rk];
          } else {
            a2TripsManualByRowRef.current[rk] = true;
          }
        }
        if (String(value).trim() === "") {
          return mergeA2TransportThenVehicle(
            next,
            a1ExtractionRef.current,
            sectionRowsRef.current,
            rawMaterialsRef.current,
            backgroundFinishedQtyRef.current,
            a2DistanceManualByRowRef.current,
            vehiclesPerRawMaterialRef.current,
            a2TotalDistanceManualByRowRef.current,
            a2TripsManualByRowRef.current
          );
        }
        return mergeA2VehicleTripsAndDistance(
          next,
          rawMaterialsRef.current,
          sectionRowsRef.current,
          backgroundFinishedQtyRef.current,
          vehiclesPerRawMaterialRef.current,
          a2TotalDistanceManualByRowRef.current,
          a2TripsManualByRowRef.current
        );
      }

      if (name.startsWith("vehicleCapacity_")) {
        return mergeA2VehicleTripsAndDistance(
          next,
          rawMaterialsRef.current,
          sectionRowsRef.current,
          backgroundFinishedQtyRef.current,
          vehiclesPerRawMaterialRef.current,
          a2TotalDistanceManualByRowRef.current,
          a2TripsManualByRowRef.current
        );
      }

      const fuelMixParsed = parseFuelMixFieldNameWithPrefix(
        A2_FUEL_MIX_FIELD,
        name
      );
      if (
        fuelMixParsed &&
        fuelMixParsed.code === A2_FUEL_TRANSPORT_CODE &&
        formData
      ) {
        const { code, row } = fuelMixParsed;
        const densityKey = `fuelDensity_${code}_${row}`;
        const densityUnitKey = `fuelDensity_unit_${code}_${row}`;
        const calorificKey = `calorificValue_${code}_${row}`;
        const calorificUnitKey = `calorificValue_unit_${code}_${row}`;
        const mixStr = String(value ?? "").trim();

        if (!mixStr) {
          next[densityKey] = "";
          next[densityUnitKey] = "";
          next[calorificKey] = "";
          next[calorificUnitKey] = "";
        } else {
          const mixLabel = getFuelMixDropdownLabelForField(
            formData,
            A2_FUEL_TRANSPORT_CODE,
            A2_FUEL_MIX_FIELD,
            mixStr
          );

          const rowData = findFuelDensityForMix(
            mixStr,
            mixLabel,
            fuelDensitiesRef.current
          );
          if (rowData) {
            const densityOpts = a2FuelUnitOptionsRef.current.density;
            const { densityStr, unitVal } = fuelDensityValueAndUnitFromCatalogRow(
              rowData,
              densityOpts
            );
            next[densityKey] = densityStr;
            next[densityUnitKey] = unitVal;
          } else {
            next[densityKey] = "";
            next[densityUnitKey] = "";
          }

          const cvRow = findCalorificValueForMix(
            mixStr,
            mixLabel,
            calorificValuesRef.current
          );
          const cvText =
            cvRow?.calorificValue != null
              ? String(cvRow.calorificValue).trim()
              : "";
          if (cvRow && cvText) {
            next[calorificKey] = cvText;
            next[calorificUnitKey] = pickCalorificUnitFromOptions(
              cvRow.unit ?? "",
              a2FuelUnitOptionsRef.current.calorific,
              unitIdByTokenRef.current
            );
          } else {
            next[calorificKey] = "";
            next[calorificUnitKey] = "";
          }
        }
      }

      return next;
    });
  };

  const addVehicleForMaterial = useCallback(
    (materialIndex: number) => {
      if (
        !formData ||
        materialIndex < 0 ||
        materialIndex >= rawMaterials.length
      ) {
        return;
      }
      const fieldNames = collectVehicleValueKeyPrefixes(
        formData.formSubSections
      );
      if (fieldNames.length === 0) return;

      setVehiclesPerRawMaterial((counts) => {
        const R = rawMaterials.length;
        if (R === 0) return counts;
        const base =
          counts.length === R
            ? counts.map((c) => Math.max(1, c))
            : Array.from({ length: R }, (_, i) => Math.max(1, counts[i] ?? 1));
        if ((base[materialIndex] ?? 0) >= A2_MAX_VEHICLES_PER_RAW_MATERIAL) {
          return counts;
        }
        const insertAt = insertIndexForNewVehicleAtMaterial(materialIndex, base);
        const nextCounts = base.map((c, i) =>
          i === materialIndex ? c + 1 : c
        );
        const newTotal = totalVehicleRows(nextCounts);

        setSectionRows((sr) => ({
          ...sr,
          [A2_VEHICLE_CODE]: newTotal,
        }));

        setValues((prev) => {
          const shifted = shiftA2VehicleValuesForInsert(
            prev,
            insertAt,
            newTotal,
            fieldNames,
            A2_VEHICLE_CODE
          );
          const rm = rawMaterials[materialIndex];
          const rk = `vehicle_rawMaterial_01_${A2_VEHICLE_CODE}_row${insertAt}`;
          const idStr = String(
            rm?.catalogId != null && rm.catalogId > 0
              ? rm.catalogId
              : rm?.id ?? ""
          );
          const next: Record<string, string> = {
            ...shifted,
            [rk]: idStr,
            [`${rk}_label`]: rm?.name ?? "",
          };

          const nv = nextCounts[materialIndex] ?? 1;
          if (nv > 1) {
            const vStart = sumPerMaterialCounts(nextCounts, materialIndex);
            for (let j = 0; j < nv; j++) {
              const flat = vStart + j;
              next[`tripsRequired_${A2_VEHICLE_CODE}_row${flat}`] = "";
              next[
                `totalDistanceTravelled_${A2_VEHICLE_CODE}_row${flat}`
              ] = "";
            }
          }

          return next;
        });

        queueMicrotask(() => {
          setValues((p) =>
            mergeA2TransportThenVehicle(
              p,
              a1ExtractionRef.current,
              {
                ...sectionRowsRef.current,
                [A2_VEHICLE_CODE]: newTotal,
              },
              rawMaterialsRef.current,
              backgroundFinishedQtyRef.current,
              a2DistanceManualByRowRef.current,
              nextCounts,
              a2TotalDistanceManualByRowRef.current,
              a2TripsManualByRowRef.current
            )
          );
        });

        return nextCounts;
      });
    },
    [formData, rawMaterials]
  );

  const removeVehicleForMaterial = useCallback(
    (materialIndex: number, vehicleSlotIndex: number) => {
      if (
        !formData ||
        materialIndex < 0 ||
        materialIndex >= rawMaterials.length
      ) {
        return;
      }
      if (vehicleSlotIndex < 0) return;

      const fieldNames = collectVehicleValueKeyPrefixes(
        formData.formSubSections
      );
      if (fieldNames.length === 0) return;

      setVehiclesPerRawMaterial((counts) => {
        const R = rawMaterials.length;
        if (R === 0) return counts;
        const base =
          counts.length === R
            ? counts.map((c) => Math.max(1, c))
            : Array.from({ length: R }, (_, i) => Math.max(1, counts[i] ?? 1));

        const n = base[materialIndex] ?? 1;
        if (n <= 1 || vehicleSlotIndex >= n) {
          return counts;
        }

        const removeAt =
          sumPerMaterialCounts(base, materialIndex) + vehicleSlotIndex;
        const oldTotal = totalVehicleRows(base);
        const nextCounts = base.map((c, i) =>
          i === materialIndex ? c - 1 : c
        );
        const newTotal = totalVehicleRows(nextCounts);

        setSectionRows((sr) => ({
          ...sr,
          [A2_VEHICLE_CODE]: newTotal,
        }));

        setValues((prev) =>
          shiftA2VehicleValuesForRemove(
            prev,
            removeAt,
            oldTotal,
            fieldNames,
            A2_VEHICLE_CODE
          )
        );

        queueMicrotask(() => {
          setValues((p) =>
            mergeA2TransportThenVehicle(
              p,
              a1ExtractionRef.current,
              {
                ...sectionRowsRef.current,
                [A2_VEHICLE_CODE]: newTotal,
              },
              rawMaterialsRef.current,
              backgroundFinishedQtyRef.current,
              a2DistanceManualByRowRef.current,
              nextCounts,
              a2TotalDistanceManualByRowRef.current,
              a2TripsManualByRowRef.current
            )
          );
        });

        return nextCounts;
      });
    },
    [formData, rawMaterials]
  );

  const addFuelForMaterial = useCallback(
    (materialIndex: number) => {
      if (
        !formData ||
        materialIndex < 0 ||
        materialIndex >= rawMaterials.length
      ) {
        return;
      }
      const fieldNames = collectFuelValueKeyPrefixesIncludingEnergy(
        formData.formSubSections
      );
      if (fieldNames.length === 0) return;

      setFuelsPerRawMaterial((counts) => {
        const R = rawMaterials.length;
        if (R === 0) return counts;
        const base =
          counts.length === R
            ? counts.map((c) => Math.max(1, c))
            : Array.from({ length: R }, (_, i) => Math.max(1, counts[i] ?? 1));
        if ((base[materialIndex] ?? 0) >= A2_MAX_FUELS_PER_RAW_MATERIAL) {
          return counts;
        }
        const insertAt = insertIndexForNewFuelAtMaterial(materialIndex, base);
        const nextCounts = base.map((c, i) =>
          i === materialIndex ? c + 1 : c
        );
        const newTotal = totalVehicleRows(nextCounts);

        setSectionRows((sr) => ({
          ...sr,
          [A2_FUEL_TRANSPORT_CODE]: newTotal,
        }));

        setValues((prev) => {
          const shifted = shiftA2FuelValuesForInsert(
            prev,
            insertAt,
            newTotal,
            fieldNames,
            A2_FUEL_TRANSPORT_CODE
          );
          const rm = rawMaterials[materialIndex];
          const rk = `fuel_rawMaterial_01_${A2_FUEL_TRANSPORT_CODE}_row${insertAt}`;
          const idStr = String(
            rm?.catalogId != null && rm.catalogId > 0
              ? rm.catalogId
              : rm?.id ?? ""
          );
          return {
            ...shifted,
            [rk]: idStr,
            [`${rk}_label`]: rm?.name ?? "",
          };
        });

        return nextCounts;
      });
    },
    [formData, rawMaterials]
  );

  const removeFuelForMaterial = useCallback(
    (materialIndex: number, fuelSlotIndex: number) => {
      if (
        !formData ||
        materialIndex < 0 ||
        materialIndex >= rawMaterials.length
      ) {
        return;
      }
      if (fuelSlotIndex < 0) return;

      const fieldNames = collectFuelValueKeyPrefixesIncludingEnergy(
        formData.formSubSections
      );
      if (fieldNames.length === 0) return;

      setFuelsPerRawMaterial((counts) => {
        const R = rawMaterials.length;
        if (R === 0) return counts;
        const base =
          counts.length === R
            ? counts.map((c) => Math.max(1, c))
            : Array.from({ length: R }, (_, i) => Math.max(1, counts[i] ?? 1));

        const n = base[materialIndex] ?? 1;
        if (n <= 1 || fuelSlotIndex >= n) {
          return counts;
        }

        const removeAt =
          sumPerMaterialCounts(base, materialIndex) + fuelSlotIndex;
        const oldTotal = totalVehicleRows(base);
        const nextCounts = base.map((c, i) =>
          i === materialIndex ? c - 1 : c
        );
        const newTotal = totalVehicleRows(nextCounts);

        const manual = a2EnergyManualByFuelFlatRowRef.current;
        const nextManual: Record<string, boolean> = {};
        for (let oldIdx = 0; oldIdx < oldTotal; oldIdx++) {
          if (oldIdx === removeAt) continue;
          const newIdx = oldIdx < removeAt ? oldIdx : oldIdx - 1;
          if (manual[`row${oldIdx}`]) {
            nextManual[`row${newIdx}`] = true;
          }
        }
        a2EnergyManualByFuelFlatRowRef.current = nextManual;

        setSectionRows((sr) => ({
          ...sr,
          [A2_FUEL_TRANSPORT_CODE]: newTotal,
        }));

        setValues((prev) =>
          shiftA2FuelValuesForRemove(
            prev,
            removeAt,
            oldTotal,
            fieldNames,
            A2_FUEL_TRANSPORT_CODE
          )
        );

        return nextCounts;
      });
    },
    [formData, rawMaterials]
  );

  /* ======================================================
     SAVE
  ====================================================== */
 const buildPayloadAndSave = async () => {
  if (!formData || !submissionId) return false;

  const sections = formData.formSubSections || [];

  const vCounts =
    vehiclesPerRawMaterial.length >= rawMaterials.length
      ? vehiclesPerRawMaterial.map((c) =>
          Math.min(
            A2_MAX_VEHICLES_PER_RAW_MATERIAL,
            Math.max(1, c)
          )
        )
      : Array.from({ length: Math.max(1, rawMaterials.length) }, () => 1);

  const fCounts =
    fuelsPerRawMaterial.length >= rawMaterials.length
      ? fuelsPerRawMaterial.map((c) =>
          Math.min(A2_MAX_FUELS_PER_RAW_MATERIAL, Math.max(1, c))
        )
      : Array.from({ length: Math.max(1, rawMaterials.length) }, () => 1);

  const fuelFlatRowCount =
    rawMaterials.length > 0
      ? fCounts.reduce((sum, c) => sum + Math.max(1, c), 0)
      : Math.max(1, sectionRows[A2_FUEL_TRANSPORT_CODE] ?? 1);

  const saveValues = hydrateA2UnitFieldsBeforeValidation({
    values,
    formData,
    rawMaterialsLength: rawMaterials.length,
    vehiclesPerRawMaterial,
    vehicleCategoryEmissionFactors,
    unitIdByToken: unitIdByTokenRef.current,
    cfUnitHintByMaterial: a2CfUnitHintByMaterialRef.current,
    cfUnitSelectOptions: a2CfUnitOptionsRef.current,
    kgCo2eUnitId: resolveKgCo2eUnitId(unitIdByTokenRef.current),
    functionalCarbonUnitValue,
    mjUnitId: getEmbodiedEnergyMjUnitId(),
    fuelFlatRowCount,
    carbonCode: A2_CARBON_CODE,
    fuelCode: A2_FUEL_TRANSPORT_CODE,
  });

  const formErrors = validateA2Form({
    sections,
    sectionRows,
    values: saveValues,
    rawMaterialsLength: rawMaterials.length,
    vehiclesPerRawMaterial,
    fuelsPerRawMaterial,
    formData,
    fuelDensityCatalog: fuelDensitiesRef.current,
    calorificValuesCatalog: calorificValuesRef.current,
  });
  if (Object.keys(formErrors).length > 0) {
    setErrors(formErrors);
    toast.error(
      getFirstFormError(formErrors) ?? "Please fix the highlighted fields."
    );
    return false;
  }
  setErrors({});

  let valuesChanged = false;
  for (const k of Object.keys(saveValues)) {
    if (saveValues[k] !== values[k]) {
      valuesChanged = true;
      break;
    }
  }
  if (valuesChanged) {
    setValues((prev) => ({ ...prev, ...saveValues }));
  }

  const toNumberOrNull = (v: unknown) => {
    if (v === "" || v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const rawMaterialFkForRow = (
    rowIndex: number,
    formVal: unknown
  ): number | null => {
    const rm = rawMaterials[rowIndex];
    if (rm?.catalogId != null && rm.catalogId > 0) return rm.catalogId;
    const n = toNumberOrNull(formVal);
    const catalogIds = catalogRawMaterialIdsRef.current;
    if (n != null && n > 0 && catalogIds.has(n)) return n;
    if (rm?.name) {
      const byName = catalogNameToIdRef.current.get(
        normalizeRawMaterialNameKey(rm.name)
      );
      if (byName != null) return byName;
    }
    return null;
  };

  const rawMaterialGroups: Record<string, unknown>[] = [];

  for (let m = 0; m < rawMaterials.length; m++) {
    const topRawId =
      rawMaterialFkForRow(
        m,
        saveValues[`rawMaterialTransportDetails_${A2_TRANSPORT_CODE}_row${m}`]
      ) ?? rawMaterialFkForRow(m, "");
    const topRawName =
      saveValues[
        `rawMaterialTransportDetails_${A2_TRANSPORT_CODE}_row${m}_label`
      ]?.trim() ||
      rawMaterials[m]?.name ||
      "";

    const transport = {
      manufacturingLat:
        saveValues[`manufacturingLatitude_${A2_TRANSPORT_CODE}_row${m}`],
      manufacturingLng:
        saveValues[`manufacturingLongitude_${A2_TRANSPORT_CODE}_row${m}`],
      state: saveValues[`state_${A2_TRANSPORT_CODE}_row${m}`],
      district: saveValues[`district_${A2_TRANSPORT_CODE}_row${m}`],
      pincode: saveValues[`pincode_${A2_TRANSPORT_CODE}_row${m}`],
      distanceKm: toStringOrZero(
        saveValues[`distanceBetweenSites_${A2_TRANSPORT_CODE}_row${m}`]
      ),
    };

    const vehicles: Record<string, unknown>[] = [];
    const vStart = sumPerMaterialCounts(vCounts, m);
    const nv = Math.max(1, vCounts[m] ?? 1);
    for (let vi = 0; vi < nv; vi++) {
      const i = vStart + vi;
      vehicles.push({
        rawMaterialId: rawMaterialFkForRow(
          m,
          saveValues[`vehicle_rawMaterial_01_${A2_VEHICLE_CODE}_row${i}`]
        ),
        rawMaterialName:
          saveValues[`vehicle_rawMaterial_01_${A2_VEHICLE_CODE}_row${i}_label`] ??
          "",
        vehicleMake: saveValues[`vehicleMake_${A2_VEHICLE_CODE}_row${i}`],
        vehicleModel: saveValues[`vehicleModel_${A2_VEHICLE_CODE}_row${i}`],
        capacity: toStringOrZero(
          saveValues[`vehicleCapacity_${A2_VEHICLE_CODE}_row${i}`]
        ),
        vehicleCategory: (() => {
          const raw = saveValues[`vehicleCategory_${A2_VEHICLE_CODE}_row${i}`];
          const pub = findVehicleCategoryEmissionFactor(
            String(raw ?? ""),
            vehicleCategoryEmissionFactors
          );
          return pub ? String(pub.id) : String(raw ?? "").trim();
        })(),
        numTripsRequired: toStringOrZero(
          saveValues[`tripsRequired_${A2_VEHICLE_CODE}_row${i}`]
        ),
        totalDistanceKm: toStringOrZero(
          saveValues[`totalDistanceTravelled_${A2_VEHICLE_CODE}_row${i}`]
        ),
      });
    }

    const fuels: Record<string, unknown>[] = [];
    const fStart = sumPerMaterialCounts(fCounts, m);
    const nf = Math.max(1, fCounts[m] ?? 1);
    for (let fi = 0; fi < nf; fi++) {
      const i = fStart + fi;
      const fuelObj: Record<string, unknown> = {
        fuelMix: saveValues[`fuelMix_01_${A2_FUEL_TRANSPORT_CODE}_row${i}`],
        quantity: toStringOrZero(
          saveValues[`fuelQuantity_${A2_FUEL_TRANSPORT_CODE}_row${i}`]
        ),
        quantityUnitId: toNumberOrZero(
          saveValues[`fuelQuantity_unit_${A2_FUEL_TRANSPORT_CODE}_row${i}`]
        ),
        fuelDensity: toStringOrZero(
          saveValues[`fuelDensity_${A2_FUEL_TRANSPORT_CODE}_row${i}`]
        ),
        fuelDensityUnitId: toNumberOrZero(
          saveValues[`fuelDensity_unit_${A2_FUEL_TRANSPORT_CODE}_row${i}`]
        ),
        calorificValue: toStringOrZero(
          saveValues[`calorificValue_${A2_FUEL_TRANSPORT_CODE}_row${i}`]
        ),
        calorificValueUnitId: toNumberOrZero(
          saveValues[`calorificValue_unit_${A2_FUEL_TRANSPORT_CODE}_row${i}`]
        ),
      };
      const ee = saveValues[`embodiedEnergy_${A2_FUEL_TRANSPORT_CODE}_row${i}`];
      const eeu =
        saveValues[`embodiedEnergy_unit_${A2_FUEL_TRANSPORT_CODE}_row${i}`];
      const eeStr = String(ee ?? "").trim();
      const eeuStr = String(eeu ?? "").trim();
      if (eeStr !== "" || eeuStr !== "") {
        fuelObj.energy = {
          embodiedEnergy:
            eeStr !== "" ? toStringOrZero(ee) : "0",
          embodiedEnergyUnitId: toNumberOrZero(eeu),
        };
      }
      fuels.push(fuelObj);
    }

    const carbon = {
      rawMaterialId: rawMaterialFkForRow(
        m,
        saveValues[`rawMaterialEmbodiedCarbonDetails_${A2_CARBON_CODE}_row${m}`]
      ),
      rawMaterialName:
        saveValues[
          `rawMaterialEmbodiedCarbonDetails_${A2_CARBON_CODE}_row${m}_label`
        ] ?? "",
      characterizationFactor: toStringOrZero(
        saveValues[`characterizationFactor_${A2_CARBON_CODE}_row${m}`]
      ),
      characterizationFactorUnit: toNumberOrZero(
        saveValues[`characterizationFactor_unit_${A2_CARBON_CODE}_row${m}`]
      ),
      embodiedCarbonReferenceQty: formatEmbodiedCarbonReferenceQtyFromUnknown(
        saveValues[`embodiedCarbon_referenceQty_${A2_CARBON_CODE}_row${m}`]
      ),
      embodiedCarbonReferenceUnit: toNumberOrZero(
        saveValues[`embodiedCarbon_referenceQty_unit_${A2_CARBON_CODE}_row${m}`]
      ),
      embodiedCarbonFunctionalQty: formatEmbodiedCarbonFunctionalUnitFromUnknown(
        saveValues[`embodiedCarbon_functionalUnit_${A2_CARBON_CODE}_row${m}`]
      ) || "0",
      embodiedCarbonFunctionalUnit: resolveEmbodiedCarbonFunctionalUnitForSave(
        saveValues[`embodiedCarbon_functionalUnit_unit_${A2_CARBON_CODE}_row${m}`],
        unitIdByTokenRef.current,
        backgroundFinishedMaterialUnitSymbol,
        resolveKgCo2eUnitId(unitIdByTokenRef.current)
      ) ?? 0,
    };

    rawMaterialGroups.push({
      rawMaterialId: topRawId,
      rawMaterialName: topRawName,
      transport,
      vehicles,
      fuels,
      carbon,
    });
  }

  const payload: Record<string, unknown> = {
    rawMaterialGroups,
    summary: {
      embodiedEnergyValue: energySummaryMj,
      embodiedEnergyUnit: perFinishedEnergyUnitValue(
        backgroundFinishedMaterialUnitSymbol
      ),
      embodiedEnergyPerDeclaredFinishedQty:
        energyPerDeclaredFinishedMaterialMj ?? null,
      embodiedCarbonTransportTotalKg: embodiedCarbonTotalKg,
      embodiedCarbonPerDeclaredFinishedQty:
        embodiedCarbonPerFinishedKg ?? null,
      embodiedCarbonValue:
        embodiedCarbonPerFinishedKg ?? embodiedCarbonTotalKg,
      embodiedCarbonUnit:
        embodiedCarbonPerFinishedKg != null
          ? perFinishedCarbonUnitValue(backgroundFinishedMaterialUnitSymbol)
          : A1A3_CARBON_NUMERATOR_UNIT,
    },
  };

  try {
    const result = await saveA2Details(submissionId, payload);
    
    if (result.success) {
      toast.success(result.message || "A2 data saved successfully!", {
        style: {
          backgroundColor: "#10b981",
          color: "#ffffff",
          fontSize: "16px",
          fontWeight: "500",
        },
      });
      lastSavedRef.current = saveValues;
      snapshotInitializedRef.current = true;
      setHasSavedOnce(true);
      setIsDirty(false);
      return true;
    } else {
      const errorMsg = result.error || "Failed to save A2 data";
      setError(errorMsg);
      toast.error(errorMsg);
      return false;
    }
  } catch (err: any) {
    console.error("Save error:", err);
    const errorMessage =
      err?.response?.data?.message ||
      err?.message ||
      "An unexpected error occurred while saving";
    setError(errorMessage);
    toast.error(errorMessage);
    return false;
  }
};

  return {
    formData,
    loading,
    error,
    errors,
    rawMaterials,
    sectionRows,
    values,
    vehiclesPerRawMaterial,
    fuelsPerRawMaterial,
    addVehicleForMaterial,
    removeVehicleForMaterial,
    addFuelForMaterial,
    removeFuelForMaterial,
    hasSavedOnce,
    isDirty,
    handleChange,
    energySummaryMj,
    energyPerDeclaredFinishedMaterialMj,
    embodiedCarbonTotalKg,
    embodiedCarbonPerFinishedKg,
    perFinishedEnergyUnitLabel: perFinishedEnergyUnitLabelDisplay,
    perFinishedCarbonUnitLabel: perFinishedCarbonUnitLabelDisplay,
    backgroundFinishedMaterialUnitSymbol,
    buildPayloadAndSave,
    vehicleCategoryEmissionFactors,
    fuelDensityCatalog,
    calorificValuesCatalog,
  };
}

